// Persistent left navigation for the admin console — this task's explicit
// brief ("persistent left navigation for admin"). Rendered ONLY inside
// AppShell when the live session's role is 'admin' (AppShell.jsx decides
// this, not this component) — a sales session never mounts this component
// at all, so "sales sees no admin nav" holds by construction, not by CSS
// hiding it (admin/CLAUDE.md: "Role gating is enforced by the API, not by
// hiding UI... never assume a hidden element is a security boundary" — this
// component's absence is a UX/navigation choice; every route it links to is
// still independently guarded server-side and, in the SPA, by each route's
// own RequireRole).
//
// Six items, this task's own explicit order: approval queue, all requests,
// catalog, cost management, users, settings — matching admin/CLAUDE.md's
// "Admin: all requests · assignment · approval queue · catalog and prices ·
// cost management (admin-only route) · users · notification settings" list,
// reordered to this task's brief rather than that file's own prose order.
//
// None of these client routes start with "/admin" (or "/auth"/"/sales"/
// "/catalog"/"/guest"/"/verify") — a real bug, found by actually navigating
// to one, not by reading the routing table: vite.config.js's dev proxy
// forwards any request whose PATH starts with one of those prefixes
// straight to the real backend, for a hard navigation/reload/direct link
// exactly as much as for a fetch() call. A client route literally named
// "/admin/requests" collided with that same prefix and returned raw
// backend JSON instead of ever reaching this SPA — same collision class as
// portal's "/guest" -> "/start" rename. Nginx fronts both the API and this
// app's static build under one host in production too (root CLAUDE.md), so
// this isn't only a dev-proxy quirk.

import { NavLink } from 'react-router-dom';
import {
  ClipboardCheck,
  Inbox,
  FileText,
  Package,
  FolderTree,
  Coins,
  Building2,
  Users,
  Landmark,
  Settings,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext.jsx';
import styles from './AdminNav.module.css';

// 'Customers' (organizations — customers/products/users multi-select
// bulk-actions task) placed right after Users: both are "who" screens, the
// existing catalog/pricing/finance items stay grouped together above them.
const ITEMS = [
  { to: '/', labelId: 'nav.admin.approvalQueue', Icon: ClipboardCheck, end: true },
  { to: '/all-requests', labelId: 'nav.admin.allRequests', Icon: Inbox },
  { to: '/all-proformas', labelId: 'nav.admin.allProformas', Icon: FileText },
  { to: '/products', labelId: 'nav.admin.catalog', Icon: Package },
  { to: '/categories', labelId: 'nav.admin.categories', Icon: FolderTree },
  { to: '/cost-management', labelId: 'nav.admin.costManagement', Icon: Coins },
  { to: '/users', labelId: 'nav.admin.users', Icon: Users },
  { to: '/customers', labelId: 'nav.admin.customers', Icon: Building2 },
  { to: '/bank-accounts', labelId: 'nav.admin.bankAccounts', Icon: Landmark },
  { to: '/settings', labelId: 'nav.admin.settings', Icon: Settings },
];

export default function AdminNav() {
  const { t } = useLanguage();

  return (
    <nav className={styles.nav} aria-label={t('nav.admin.approvalQueue')}>
      <ul className={styles.list}>
        {ITEMS.map(({ to, labelId, Icon, end }) => (
          <li key={to}>
            <NavLink
              to={to}
              end={end}
              className={({ isActive }) => `${styles.link} ${isActive ? styles.linkActive : ''}`}
            >
              <Icon size={18} aria-hidden="true" />
              {t(labelId)}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
